/*!
 * FrontAlign v1.1.0
 * (c) Eyruz Badalzada
 * Released under the MIT License
 * https://www.frontalign.dev
 */

import Toast from "../components/Toast.js";
import ScrollBarHelper from "../utils/ScrollBarHelper.js";
import DomUtils from "../utils/DomUtils.js";
export default class Components {
  
  /**
   * Initializes responsive navbar toggle interactions,
   * Returns a cleanup disposer function.
   */
  static navbar() {
    const CONFIG = {
      trigger: '[fa-toggle="navbar"]',

      selectors: {
        menu: ".navbar-menu",
      },

      classes: {
        toggle: "is-active",
      },
    };

    const lockMap = new WeakMap();

    const handler = async (e) => {
      const btn = e.target.closest(CONFIG.trigger);
      if (!btn) return;

      e.preventDefault();
      const targetSelector = btn.getAttribute("data-target");
      if (!targetSelector) return;

      const menu = document.querySelector(targetSelector);
      if (!menu) return;

      if (lockMap.get(menu)) return;
      lockMap.set(menu, true);

      const isOpen = btn.classList.contains(CONFIG.classes.toggle);

      document.querySelectorAll(CONFIG.selectors.menu).forEach((el) => {
        if (el !== menu)
          DomUtils.slideUp(el).finally(() => lockMap.set(el, false));
      });
      document.querySelectorAll(CONFIG.trigger).forEach((el) => {
        if (el !== btn) el.classList.remove(CONFIG.classes.toggle);
      });

      if (isOpen) {
        btn.classList.remove(CONFIG.classes.toggle);
        await DomUtils.slideUp(menu, 300);
      } else {
        btn.classList.add(CONFIG.classes.toggle);
        await DomUtils.slideDown(menu, 300, "flex");
        const disposeEsc = DomUtils.closeOnEscape(menu, btn, 300);
        menu._disposeEsc = disposeEsc;
      }

      lockMap.set(menu, false);
    };

    document.body.addEventListener("click", handler);
    return () => document.body.removeEventListener("click", handler);
  }

  /**
   * Initializes dismissible alerts,
   * Returns a cleanup disposer function.
   */
  static alert() {
    const CONFIG = {
      selector: '[fa-component="alert"]',

      selectors: {
        closeBtn: ".alert-close",
      },

      attrs: {
        persistent: "data-persistent",
        alertId: "data-alert",
        animation: "data-close-animation",
      },
    };

    const storage = {
      get: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (e) {
          return null;
        }
      },
      set: (key, val) => {
        try {
          localStorage.setItem(key, val);
        } catch (e) {}
      },
    };

    document
      .querySelectorAll(`${CONFIG.selector}[${CONFIG.attrs.persistent}="true"]`)
      .forEach((alert) => {
        const key = alert.getAttribute(CONFIG.attrs.alertId);
        if (key && storage.get(`dismissed-${key}`)) alert.remove();
      });
    const clickHandler = (evt) => {
      const closeBtn = evt.target.closest(CONFIG.selectors.closeBtn);
      if (!closeBtn) return;

      const alertElement = closeBtn.closest(CONFIG.selector);
      if (!alertElement) return;

      evt.preventDefault();

      const closeEvent = new CustomEvent("fa.alert.close", {
        detail: { alert: alertElement },
      });
      alertElement.dispatchEvent(closeEvent);
      if (closeEvent.defaultPrevented) return;

      if (alertElement.getAttribute(CONFIG.attrs.persistent) === "true") {
        const key = alertElement.getAttribute(CONFIG.attrs.alertId);
        if (key) {
          storage.set(`dismissed-${key}`, "true");
        }
      }

      const dismissMode = alertElement.getAttribute(CONFIG.attrs.animation);
      let isRemoved = false;

      const finalizeDismiss = () => {
        if (isRemoved) return;
        isRemoved = true;
        document.dispatchEvent(
          new CustomEvent("fa.alert.closed", {
            detail: { alert: alertElement },
          }),
        );
        alertElement.remove();
      };

      if (dismissMode === "slide") {
        DomUtils.slideUp(alertElement, 300).then(finalizeDismiss);
      } else {
        alertElement.style.transition = "opacity 0.25s ease";
        alertElement.style.opacity = "0";
        alertElement.addEventListener("transitionend", finalizeDismiss, {
          once: true,
        });
        setTimeout(finalizeDismiss, 350);
      }
    };

    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }

  /**
   * Initializes collapsible elements,
   * Returns a cleanup disposer function.
   */
  static collapse() {
    const CONFIG = {
      trigger: '[fa-toggle="collapse"]',

      attrs: {
        target: "data-target",
      },

      classes: {
        open: "is-open",
      },

      defaults: {
        duration: 300,
      },
    };

    const locks = new WeakSet();

    const updateState = (btn, target, isOpen) => {
      btn.classList.toggle(CONFIG.classes.open, isOpen);
      btn.setAttribute("aria-expanded", isOpen ? "true" : "false");

      if (target.id) {
        btn.setAttribute("aria-controls", target.id);
      }

      target.classList.toggle(CONFIG.classes.open, isOpen);
      target.setAttribute("data-state", isOpen ? "open" : "closed");
    };

    const setInitialState = () => {
      document.querySelectorAll(CONFIG.trigger).forEach((btn) => {
        const targetSelector = btn.getAttribute(CONFIG.attrs.target);
        if (!targetSelector) return;

        const target = document.querySelector(targetSelector);
        if (!target) return;

        const isOpen =
          btn.classList.contains(CONFIG.classes.open) ||
          target.classList.contains(CONFIG.classes.open) ||
          target.getAttribute("data-open") === "true";

        updateState(btn, target, isOpen);

        if (isOpen) {
          target.style.display = "block";
        } else {
          target.style.display = "none";
        }
      });
    };

    const toggleCollapse = async (btn) => {
      if (btn.disabled || btn.getAttribute("aria-disabled") === "true") return;

      const targetSelector = btn.getAttribute(CONFIG.attrs.target);
      if (!targetSelector) return;

      const target = document.querySelector(targetSelector);
      if (!target) return;

      if (locks.has(target)) return;
      locks.add(target);

      const isOpen = target.classList.contains(CONFIG.classes.open);

      const eventName = isOpen ? "close" : "open";
      const startEvent = new CustomEvent(`fa.collapse.${eventName}`, {
        detail: { trigger: btn, target },
        bubbles: true,
        cancelable: true,
      });

      target.dispatchEvent(startEvent);

      if (startEvent.defaultPrevented) {
        locks.delete(target);
        return;
      }

      updateState(btn, target, !isOpen);

      if (isOpen) {
        await DomUtils.slideUp(target, CONFIG.defaults.duration);
      } else {
        await DomUtils.slideDown(target, CONFIG.defaults.duration);
      }

      target.dispatchEvent(
        new CustomEvent(`fa.collapse.${isOpen ? "closed" : "opened"}`, {
          detail: { trigger: btn, target },
          bubbles: true,
        }),
      );

      locks.delete(target);
    };

    const clickHandler = (evt) => {
      const btn = evt.target.closest(CONFIG.trigger);
      if (!btn) return;

      evt.preventDefault();
      toggleCollapse(btn);
    };

    setInitialState();

    document.addEventListener("click", clickHandler);

    return () => {
      document.removeEventListener("click", clickHandler);
    };
  }

  /**
   * Initializes draggable horizontal swiper behavior,
   * Adds pointer-based touch and mouse dragging support.
   */
  static swiper(element) {
    if (!element) return;

    const wrapper = element.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;
    let moved = false;
    const DRAG_THRESHOLD = 3; // px

    const onPointerDown = (e) => {
      isDragging = true;
      moved = false;

      element.classList.add("is-dragging");

      startX = e.clientX;
      scrollStart = wrapper.scrollLeft;

      wrapper.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;

      const delta = e.clientX - startX;

      if (Math.abs(delta) > DRAG_THRESHOLD) {
        moved = true;
      }

      const movement = delta * 1.15;
      wrapper.scrollLeft = scrollStart - movement;
    };

    const onPointerUpOrCancel = (e) => {
      if (!isDragging) return;

      isDragging = false;
      element.classList.remove("is-dragging");

      wrapper.releasePointerCapture(e.pointerId);

      // If dragged, prevent click events inside the swiper
      if (moved) {
        const preventClick = (ev) => {
          ev.preventDefault();
          ev.stopImmediatePropagation();
        };

        wrapper.addEventListener("click", preventClick, {
          capture: true,
          once: true,
        });
      }
    };

    wrapper.addEventListener("pointerdown", onPointerDown, { passive: true });
    wrapper.addEventListener("pointermove", onPointerMove, { passive: false });
    wrapper.addEventListener("pointerup", onPointerUpOrCancel, {
      passive: true,
    });
    wrapper.addEventListener("pointercancel", onPointerUpOrCancel, {
      passive: true,
    });
    wrapper.addEventListener("pointerleave", onPointerUpOrCancel, {
      passive: true,
    });

    return {
      dispose: () => {
        wrapper.removeEventListener("pointerdown", onPointerDown);
        wrapper.removeEventListener("pointermove", onPointerMove);
        wrapper.removeEventListener("pointerup", onPointerUpOrCancel);
        wrapper.removeEventListener("pointercancel", onPointerUpOrCancel);
        wrapper.removeEventListener("pointerleave", onPointerUpOrCancel);
      },
    };
  }

  /**
   * Initializes dropdown interactions,
   * Returns a cleanup disposer function.
   */
  static dropdown() {
    const CONFIG = {
      selector: ".dropdown",

      trigger: '[fa-toggle="dropdown"]',

      selectors: {
        menu: ".dropdown-menu",
      },

      classes: {
        active: "is-active",
      },

      attrs: {
        triggerMode: "data-trigger",
      },
    };

    const toggleDropdown = (drop, show) => {
      show
        ? drop.classList.add(CONFIG.classes.active)
        : drop.classList.remove(CONFIG.classes.active);
      const trigger = drop.querySelector(CONFIG.trigger);
      if (trigger)
        trigger.setAttribute("aria-expanded", show ? "true" : "false");
    };

    const closeAll = (except = null) => {
      document
        .querySelectorAll(`${CONFIG.selector}.${CONFIG.classes.active}`)
        .forEach((drop) => {
          if (drop !== except) toggleDropdown(drop, false);
        });
    };
    const clickHandler = (evt) => {
      const trigger = evt.target.closest(CONFIG.trigger);
      const isInsideMenu = evt.target.closest(CONFIG.selectors.menu);

      if (isInsideMenu) {
        const link = evt.target.closest("a");
        if (link) {
          const drop = isInsideMenu.closest(CONFIG.selector);
          if (drop) toggleDropdown(drop, false);
        }
        return;
      }

      if (!trigger) {
        closeAll();
        return;
      }

      const drop = trigger.closest(CONFIG.selector);
      if (!drop) return;

      closeAll(drop);
      const isActive = drop.classList.contains(CONFIG.classes.active);
      toggleDropdown(drop, !isActive);
    };

    const keyHandler = (evt) => {
      if (evt.key === "Escape" || evt.key === "Esc") closeAll();
    };

    document.addEventListener("click", clickHandler);
    document.addEventListener("keydown", keyHandler);

    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      const hoverTimers = new WeakMap();

      const mouseoverHandler = (evt) => {
        const hoverEl = evt.target.closest(
          `${CONFIG.selector}[${CONFIG.attrs.triggerMode}="hover"]`,
        );
        if (!hoverEl) return;
        if (hoverTimers.has(hoverEl)) {
          clearTimeout(hoverTimers.get(hoverEl));
          hoverTimers.delete(hoverEl);
        }
        closeAll(hoverEl);
        toggleDropdown(hoverEl, true);
      };

      const mouseoutHandler = (evt) => {
        const hoverEl = evt.target.closest(
          `${CONFIG.selector}[${CONFIG.attrs.triggerMode}="hover"]`,
        );
        if (!hoverEl) return;
        if (hoverTimers.has(hoverEl)) clearTimeout(hoverTimers.get(hoverEl));
        const timer = setTimeout(() => {
          if (!hoverEl.matches(":hover") && !hoverEl.querySelector(":hover")) {
            toggleDropdown(hoverEl, false);
          }
          hoverTimers.delete(hoverEl);
        }, 150);
        hoverTimers.set(hoverEl, timer);
      };

      document.addEventListener("mouseover", mouseoverHandler);
      document.addEventListener("mouseout", mouseoutHandler);

      return () => {
        document.removeEventListener("click", clickHandler);
        document.removeEventListener("keydown", keyHandler);
        document.removeEventListener("mouseover", mouseoverHandler);
        document.removeEventListener("mouseout", mouseoutHandler);
      };
    }

    return () => {
      document.removeEventListener("click", clickHandler);
      document.removeEventListener("keydown", keyHandler);
    };
  }

  /**
   * Initializes drawer interactions/component,
   * Returns a cleanup disposer function.
   */
  static drawer() {
    const CONFIG = {
      parent: ".drawer",

      selectors: {
        dismissBtn: ".drawer-close",
        panel: ".drawer-panel",
        focusable:
          'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      },

      classes: {
        open: "is-open",
      },

      attrs: {
        target: "data-target",
      },

      defaults: {
        transitionDuration: 420,
      },
    };

    const body = document.body;
    let activeDrawer = null;
    let drawerLock = false;
    const scrollbar = new ScrollBarHelper();

    const lockBodyScroll = () => {
      scrollbar.hide();
    };

    const unlockBodyScroll = () => {
      scrollbar.reset();
    };

    const trapFocus = (drawer) => {
      const focusables = Array.from(
        drawer.querySelectorAll(CONFIG.selectors.focusable),
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) {
        drawer.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      const keyHandler = (e) => {
        if (e.key !== "Tab") return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      drawer._focusHandler = keyHandler;
      drawer.addEventListener("keydown", keyHandler);
      first.focus();
    };

    const releaseFocusTrap = (drawer) => {
      if (drawer._focusHandler) {
        drawer.removeEventListener("keydown", drawer._focusHandler);
        drawer._focusHandler = null;
      }
    };

    const isNavigationalLink = (link) => {
      if (!link) return false;
      if (link.hasAttribute("fa-toggle") || link.closest("[fa-toggle]")) {
        return false;
      }
      const href = link.getAttribute("href");
      if (href === null) return false; // no href per spec, not a hyperlink
      const trimmed = href.trim();
      if (trimmed === "" || trimmed === "#") return false;
      if (trimmed.toLowerCase().startsWith("javascript:")) return false;
      return true;
    };

    const openDrawer = (drawer) => {
      if (drawerLock || drawer.classList.contains(CONFIG.classes.open)) return;

      const openEvent = new CustomEvent("fa.drawer.open", {
        detail: { drawer },
        bubbles: true,
        cancelable: true,
      });
      drawer.dispatchEvent(openEvent);
      if (openEvent.defaultPrevented) return;

      drawerLock = true;
      activeDrawer = drawer;
      drawer.setAttribute("aria-modal", "true");
      drawer.setAttribute("role", "dialog");
      drawer.setAttribute("tabindex", "-1");

      if (!drawer.classList.contains("has-no-backdrop")) {
        lockBodyScroll();
      }

      DomUtils.afterPaint(() => {
        drawer.classList.add(CONFIG.classes.open);
      });

      setTimeout(() => {
        trapFocus(drawer);
        drawerLock = false;
        drawer.dispatchEvent(
          new CustomEvent("fa.drawer.opened", {
            detail: { drawer },
            bubbles: true,
          }),
        );
      }, CONFIG.defaults.transitionDuration + 50);
    };

    const closeDrawer = (drawer) => {
      if (!drawer.classList.contains(CONFIG.classes.open)) return;
      if (activeDrawer === drawer) activeDrawer = null;
      const closeEvent = new CustomEvent("fa.drawer.close", {
        detail: { drawer },
        bubbles: true,
        cancelable: true,
      });
      drawer.dispatchEvent(closeEvent);
      if (closeEvent.defaultPrevented) return;

      drawerLock = true;
      drawer.classList.remove(CONFIG.classes.open);
      drawer.removeAttribute("aria-modal");
      drawer.removeAttribute("role");
      releaseFocusTrap(drawer);

      let transitioned = false;
      const cleanup = () => {
        if (transitioned) return;
        transitioned = true;
        drawerLock = false;
        if (!drawer.classList.contains("has-no-backdrop")) {
          unlockBodyScroll();
        }
        drawer.dispatchEvent(
          new CustomEvent("fa.drawer.closed", {
            detail: { drawer },
            bubbles: true,
          }),
        );
      };

      const handler = (e) => {
        if (
          e.target !== drawer &&
          e.target !== drawer.querySelector(CONFIG.selectors.panel)
        )
          return;
        drawer.removeEventListener("transitionend", handler);
        cleanup();
      };

      drawer.addEventListener("transitionend", handler);
      setTimeout(cleanup, CONFIG.defaults.transitionDuration + 50);
    };

    const clickHandler = (evt) => {
      const trigger = evt.target.closest('[fa-toggle="drawer"]');
      const dismissBtn = evt.target.closest(CONFIG.selectors.dismissBtn);

      if (trigger) {
        evt.preventDefault();
        const selector = trigger.getAttribute(CONFIG.attrs.target);
        const drawer = selector
          ? document.querySelector(selector)
          : trigger.closest(CONFIG.parent);
        if (!drawer) return;
        openDrawer(drawer);
        return;
      }

      if (dismissBtn) {
        evt.preventDefault();
        const drawer = dismissBtn.closest(CONFIG.parent);
        if (drawer) closeDrawer(drawer);
        return;
      }

      if (activeDrawer) {
        const link = evt.target.closest("a");
        if (link && activeDrawer.contains(link) && isNavigationalLink(link)) {
          closeDrawer(activeDrawer);
        }
      }

      if (
        activeDrawer &&
        activeDrawer.classList.contains(CONFIG.classes.open)
      ) {
        const panel = activeDrawer.querySelector(CONFIG.selectors.panel);
        if (
          panel &&
          !panel.contains(evt.target) &&
          !evt.target.closest('[fa-toggle="drawer"]')
        ) {
          closeDrawer(activeDrawer);
        }
      }
    };

    const keyHandler = (evt) => {
      if ((evt.key === "Escape" || evt.key === "Esc") && activeDrawer) {
        closeDrawer(activeDrawer);
      }
    };

    document.addEventListener("click", clickHandler);
    document.addEventListener("keydown", keyHandler);

    return () => {
      document.removeEventListener("click", clickHandler);
      document.removeEventListener("keydown", keyHandler);
      if (activeDrawer) closeDrawer(activeDrawer);
      unlockBodyScroll();
    };
  }

  /**
   * Initializes nav tab interactions/component ,
   * Returns a cleanup disposer function.
   */

  static tabview(tabContainer) {
    // SSR guard
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    if (!tabContainer) return;

    const CONFIG = {
      attrs: {
        tab: "data-tab",
        indicator: "data-tab-indicator",
      },
      classes: {
        active: "is-active",
        open: "is-open",
        underline: "tab-indicator",
        instant: "is-instant",
      },
    };

    const tabs = Array.from(
      tabContainer.querySelectorAll(`[${CONFIG.attrs.tab}]`),
    );
    if (!tabs.length) return;

    const indicator = tabContainer.getAttribute(CONFIG.attrs.indicator);
    const hasUiIndicator = indicator === "true" || indicator === "";

    let underline = tabContainer.querySelector(`.${CONFIG.classes.underline}`);
    const weCreatedUnderline = hasUiIndicator && !underline;

    if (hasUiIndicator) {
      if (getComputedStyle(tabContainer).position === "static") {
        tabContainer.style.position = "relative";
      }
      if (!underline) {
        underline = document.createElement("div");
        underline.className = CONFIG.classes.underline;
        tabContainer.appendChild(underline);
      }
      underline.style.display = "";
    } else if (underline) {
      underline.style.display = "none";
    }

    const panelMap = new Map();
    tabs.forEach((tab) => {
      const sel = tab.getAttribute(CONFIG.attrs.tab);
      const panel = sel ? document.querySelector(sel) : null;
      if (panel) panelMap.set(sel, panel);

      if (!tab.id) tab.id = `tab-${Math.random().toString(36).slice(2, 9)}`;
      tab.setAttribute("role", "tab");

      if (panel) {
        if (!panel.id)
          panel.id = `panel-${Math.random().toString(36).slice(2, 9)}`;
        tab.setAttribute("aria-controls", panel.id);
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tab.id);
      }
    });

    tabContainer.setAttribute("role", "tablist");

    const underlineSize =
      parseFloat(
        getComputedStyle(tabContainer).getPropertyValue("--tab-underline-size"),
      ) || 3;

    let pendingRaf = null;
    let disposed = false;

    const measure = (el) => {
      const containerRect = tabContainer.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      return {
        left: elRect.left - containerRect.left + tabContainer.scrollLeft,
        top: elRect.top - containerRect.top + tabContainer.scrollTop,
        width: elRect.width,
        height: elRect.height,
      };
    };

    const readLayout = () => {
      const style = getComputedStyle(tabContainer);
      const flexDir = style.flexDirection;
      return {
        vertical: flexDir === "column" || flexDir === "column-reverse",
        rtl: style.direction === "rtl",
      };
    };

    function updateUnderline(instant = false) {
      if (!hasUiIndicator || !underline) return;

      const activeTab = tabContainer.querySelector(`.${CONFIG.classes.active}`);
      if (!activeTab) return;

      if (activeTab.offsetWidth === 0 && activeTab.offsetHeight === 0) return;

      if (pendingRaf !== null) cancelAnimationFrame(pendingRaf);

      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;

        const { left, top, width, height } = measure(activeTab);
        const { vertical, rtl } = readLayout();

        let x, y, w, h;

        if (vertical) {
          // Side indicator: inline-start edge (left in LTR, right in RTL).
          w = Math.round(underlineSize);
          h = Math.round(height);
          y = Math.round(top);
          x = rtl ? Math.round(left + width - underlineSize) : Math.round(left);
        } else {
          // Default: bottom edge underline.
          w = Math.round(width);
          h = Math.round(underlineSize);
          x = Math.round(left);
          y = Math.round(top + height - underlineSize);
        }

        if (instant) underline.classList.add(CONFIG.classes.instant);

        underline.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        underline.style.width = `${w}px`;
        underline.style.height = `${h}px`;

        if (instant) {
          void underline.offsetWidth;
          requestAnimationFrame(() =>
            underline.classList.remove(CONFIG.classes.instant),
          );
        }
      });
    }

    // Single source of truth for switching tabs
    function setActiveTab(tab, { focus = false } = {}) {
      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle(CONFIG.classes.active, isActive);
        t.setAttribute("aria-selected", String(isActive));
        t.tabIndex = isActive ? 0 : -1;
      });

      panelMap.forEach((panel) => panel.classList.remove(CONFIG.classes.open));

      const sel = tab.getAttribute(CONFIG.attrs.tab);
      const panel = panelMap.get(sel);
      if (panel) panel.classList.add(CONFIG.classes.open);

      updateUnderline();
      if (focus) tab.focus();
    }

    const initialActive =
      tabContainer.querySelector(`.${CONFIG.classes.active}`) || tabs[0];
    setActiveTab(initialActive);

    if (underline) updateUnderline(true);

    const clickHandler = (evt) => {
      const tab = evt.target.closest(`[${CONFIG.attrs.tab}]`);
      if (!tab || !tabContainer.contains(tab)) return;
      if (tab.classList.contains(CONFIG.classes.active)) return;

      evt.preventDefault();
      setActiveTab(tab);
    };

    // ArrowLeft/ArrowRight/Home/End — roving tabindex, automatic activation
    const keydownHandler = (evt) => {
      const currentTab = evt.target.closest(`[${CONFIG.attrs.tab}]`);
      if (!currentTab || !tabContainer.contains(currentTab)) return;

      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex === -1) return;

      let nextIndex = null;
      switch (evt.key) {
        case "ArrowRight":
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case "ArrowLeft":
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      evt.preventDefault();
      setActiveTab(tabs[nextIndex], { focus: true });
    };

    tabContainer.addEventListener("click", clickHandler);
    tabContainer.addEventListener("keydown", keydownHandler);

    // Enable ResizeObserver
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateUnderline())
        : null;
    if (resizeObserver) {
      resizeObserver.observe(tabContainer);
      tabs.forEach((t) => resizeObserver.observe(t));
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!disposed) updateUnderline();
      });
    }

    return {
      dispose: () => {
        disposed = true;
        if (pendingRaf !== null) cancelAnimationFrame(pendingRaf);
        tabContainer.removeEventListener("click", clickHandler);
        tabContainer.removeEventListener("keydown", keydownHandler);
        if (resizeObserver) resizeObserver.disconnect();
        if (
          weCreatedUnderline &&
          underline &&
          underline.parentNode === tabContainer
        ) {
          underline.remove();
        }
      },
    };
  }

  /**
   * Formats badge counters and automatically,
   * limits large values to 99+.
   */
  static badge(element) {
    const countRaw = element.getAttribute("data-count");
    if (!countRaw) return;

    const count = Number(countRaw);
    if (Number.isNaN(count)) return;

    element.textContent = count < 100 ? count : "99+";
    element.setAttribute("aria-label", `${count}`);
  }

  /**
   * Initializes accordion interactions,
   * Returns a cleanup disposer function.
   */
  static accordion() {
    const CONFIG = {
      selector: '[fa-component="accordion"]',
      classes: {
        open: "is-open",
        active: "is-active",
        disabled: "is-disabled",
      },

      selectors: {
        toggler: ".accordion-toggler",
        content: ".accordion-content",
        icon: ".accordion-icon",
      },

      attrs: {
        expanded: "aria-expanded",
      },
    };

    const clickHandler = async (evt) => {
      const toggler = evt.target.closest(CONFIG.selectors.toggler);
      if (!toggler) return;

      const root = toggler.closest(CONFIG.selector);
      if (!root) return;

      if (root._faAccordionLock) return;
      root._faAccordionLock = true;

      const singleOpen = root.getAttribute("data-single-open") !== "false";
      const isOpen = toggler.classList.contains(CONFIG.classes.open);

      if (isOpen) {
        await accordionClose(toggler);
      } else {
        const openedSuccessfully = await accordionOpen(toggler);
        if (openedSuccessfully && singleOpen) {
          const others = [
            ...root.querySelectorAll(CONFIG.selectors.toggler),
          ].filter(
            (btn) =>
              btn !== toggler && btn.classList.contains(CONFIG.classes.open),
          );
          await Promise.all(others.map((btn) => accordionClose(btn)));
        }
      }

      root._faAccordionLock = false;
    };

    document.querySelectorAll(CONFIG.selector).forEach((root) => {
      if (root.getAttribute("data-open-stay") !== "true") return;
      const first = root.querySelector(CONFIG.selectors.toggler);
      if (!first) return;
      first.classList.add(CONFIG.classes.open);
      const content = first.parentElement.querySelector(
        CONFIG.selectors.content,
      );
      if (content) content.style.display = "block";
    });

    document.addEventListener("click", clickHandler);

    return () => document.removeEventListener("click", clickHandler);

    async function accordionClose(btn) {
      const item = btn.parentElement;
      const content = item.querySelector(CONFIG.selectors.content);
      if (!content) return false;

      const closeEvent = new CustomEvent("fa.accordion.close", {
        detail: { trigger: btn, content, item },
        bubbles: true,
        cancelable: true,
      });
      item.dispatchEvent(closeEvent);
      if (closeEvent.defaultPrevented) return false;

      btn.classList.remove(CONFIG.classes.open);
      await DomUtils.slideUp(content, 200);

      item.dispatchEvent(
        new CustomEvent("fa.accordion.closed", {
          detail: { trigger: btn, content, item },
          bubbles: true,
        }),
      );

      return true;
    }

    async function accordionOpen(btn) {
      const item = btn.parentElement;
      const content = item.querySelector(CONFIG.selectors.content);
      if (!content) return false;

      const openEvent = new CustomEvent("fa.accordion.open", {
        detail: { trigger: btn, content, item },
        bubbles: true,
        cancelable: true,
      });
      item.dispatchEvent(openEvent);
      if (openEvent.defaultPrevented) return false;

      btn.classList.add(CONFIG.classes.open);
      await DomUtils.slideDown(content, 200);

      item.dispatchEvent(
        new CustomEvent("fa.accordion.opened", {
          detail: { trigger: btn, content, item },
          bubbles: true,
        }),
      );

      return true;
    }
  }
}
